/**
 * MarketplaceHome -- Public marketplace catalog (marketplace.example.com)
 * Hero + search + featured strip + category filter + app grid
 * Uses unified CMS API
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';

import { T } from '../styles/tokens';
import { TenantLogo, tenantName } from '../components/TenantLogo';
import { Button } from '../components/ui';
import i18next from 'i18next';
const API_BASE = '/api/v1/cms';

interface MarketplaceItem {
  id: string; slug: string; content_type_id: string; category_id: string;
  status: string; is_featured: number; is_popular: number; sort_order: number;
  view_count: number; like_count: number;
  tags: string; metadata: string; published_at: string;
  title: string; excerpt: string; features_json: string; custom_fields: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
}

interface Category {
  id: string; slug: string; icon: string; color: string; sort_order: number;
  name: string; description: string; entry_count: number;
}

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return navigator.language?.startsWith('pt') ? 'pt-BR' : 'en';
}

function parseMeta(item: MarketplaceItem): Record<string, any> {
  try { return JSON.parse(item.metadata || '{}'); } catch { return {}; }
}

function parseCustomFields(item: MarketplaceItem): Record<string, any> {
  try { return JSON.parse(item.custom_fields || '{}'); } catch { return {}; }
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  hero: { textAlign: 'center' as const, padding: '60px 20px 40px', marginBottom: 32 } as any,
  heroTitle: { fontSize: 42, fontWeight: 800, lineHeight: 1.1, margin: '0 0 16px', background: 'linear-gradient(135deg, #E8EEF9, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  heroSub: { fontSize: 18, color: T.textSecondary, maxWidth: 600, margin: '0 auto 32px', lineHeight: 1.6 } as any,
  searchWrap: { maxWidth: 560, margin: '0 auto', position: 'relative' as const } as any,
  searchInput: { width: '100%', padding: '14px 20px 14px 48px', fontSize: 16, background: T.bgCardHover, border: `1px solid ${T.bgActive}`, borderRadius: 12, color: T.text, outline: 'none', boxSizing: 'border-box' as const } as any,
  searchIcon: { position: 'absolute' as const, left: 16, top: '50%', transform: 'translateY(-50%)', color: T.textMuted } as any,
  featuredStrip: { display: 'flex', gap: 20, overflowX: 'auto' as const, padding: '0 0 24px', marginBottom: 32, scrollbarWidth: 'none' as any } as any,
  featuredCard: { minWidth: 280, flex: '0 0 280px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24, cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s' } as any,
  catBar: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 32 } as any,
  catPill: (active: boolean, color: string) => ({
    padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    background: active ? `${color}30` : T.bgCard,
    color: active ? color : T.textSecondary,
    border: active ? `1px solid ${color}50` : `1px solid ${T.border}`,
    transition: 'all 0.15s',
  }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 48 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s' } as any,
  cardTop: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 } as any,
  cardIcon: { width: 48, height: 48, borderRadius: 10, objectFit: 'cover' as const, background: T.border, flexShrink: 0 } as any,
  cardIconPlaceholder: { width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg, #1a1e3a, #12162b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: T.bgSecondary, flexShrink: 0 } as any,
  cardName: { fontSize: 16, fontWeight: 700, color: T.text, margin: 0, lineHeight: 1.3 } as any,
  cardTagline: { fontSize: 13, color: T.textSecondary, lineHeight: 1.4, margin: '4px 0 0' } as any,
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 14 } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${color}20`, color }),
  cardBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${T.border}`, paddingTop: 12 } as any,
  installBtn: { padding: '7px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer', background: T.primary, color: T.text, transition: 'opacity 0.15s' } as any,
  planBadge: { fontSize: 11, color: T.textMuted } as any,
  popularStar: { color: T.warning, fontSize: 14 } as any,
  skeleton: { background: `linear-gradient(90deg, ${T.bgCard} 25%, T.border 50%, T.bgCard 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 8 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 } as any,
};

function ItemCard({ item, locale }: { item: MarketplaceItem; locale: string }) {
  const meta = parseMeta(item);
  const iconUrl = meta.icon_url || '';
  const version = meta.version || '';
  const minPlan = meta.min_plan || 'free';
  const status = meta.status || 'stable';
  const name = item.title || '';
  const tagline = item.excerpt || '';

  return (
    <div style={S.card}
      onClick={() => { window.location.href = `/${locale}/${item.slug}`; }}
      onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.borderColor = T.primaryBg; }}
      onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = T.border; }}>
      <div style={S.cardTop}>
        {iconUrl
          ? <img style={S.cardIcon} src={iconUrl} alt={name} loading="lazy" />
          : <div style={S.cardIconPlaceholder}>{(name || '?')[0]}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h3 style={S.cardName}>{name}</h3>
            {item.is_popular === 1 && <span style={S.popularStar} title="Popular">*</span>}
          </div>
          <p style={S.cardTagline}>{tagline}</p>
        </div>
      </div>
      <div style={S.cardMeta}>
        {item.category_name && <span style={S.badge(item.category_color || T.primary)}>{item.category_name}</span>}
        <span style={S.badge(status === 'stable' ? T.success : status === 'beta' ? T.warning : T.textMuted)}>{status}</span>
        {version && <span style={{ fontSize: 11, color: T.textMuted }}>v{version}</span>}
      </div>
      <div style={S.cardBottom}>
        <Button variant="primary" size="sm" onClick={(e: any) => { e.stopPropagation(); window.location.href = `/${locale}/${item.slug}`; }} style={S.installBtn}>
          Instalar
        </Button>
        <span style={S.planBadge}>{minPlan === 'free' ? i18next.t('billing:free_plan') : minPlan}</span>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{ ...S.card, cursor: 'default' }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
        <div style={{ ...S.skeleton, width: 48, height: 48, borderRadius: 10 }} />
        <div style={{ flex: 1 }}>
          <div style={{ ...S.skeleton, width: '60%', height: 16, marginBottom: 8 }} />
          <div style={{ ...S.skeleton, width: '90%', height: 12 }} />
        </div>
      </div>
      <div style={{ ...S.skeleton, width: '40%', height: 20, marginBottom: 14 }} />
      <div style={{ ...S.skeleton, width: '100%', height: 32 }} />
    </div>
  );
}

export function MarketplaceHome({ locale: propLocale }: { path?: string; locale?: string }) {
  const locale = propLocale || getLocale();
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [featured, setFeatured] = useState<MarketplaceItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/entries?contentType=marketplace&status=published&locale=${locale}&limit=100`).then(r => r.json()),
      fetch(`${API_BASE}/categories?contentType=marketplace&locale=${locale}`).then(r => r.json()),
    ]).then(([entriesRes, catsRes]) => {
      const allItems = entriesRes?.data?.entries || [];
      setItems(allItems);
      setFeatured(allItems.filter((a: MarketplaceItem) => a.is_featured === 1).slice(0, 4));
      setCategories(catsRes?.data?.categories || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [locale]);

  const filtered = items.filter(a => {
    if (activeCat && a.category_slug !== activeCat) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.title || '').toLowerCase().includes(q) || (a.excerpt || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* Header */}
        <div style={S.header}>
          <a href={`/${locale}`} style={S.logo}>
            <TenantLogo size={28} />
            <span>{tenantName()} <span style={S.logoAccent}>Marketplace</span></span>
          </a>
        </div>

        {/* Hero */}
        <div style={S.hero}>
          <h1 style={S.heroTitle}>Marketplace</h1>
          <p style={S.heroSub}>Deploy aplicacoes, cerebros, skills e templates com um clique na WhiteLabelClaw Cloud</p>
          <div style={S.searchWrap}>
            <span style={S.searchIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input style={S.searchInput} type="text" placeholder="Buscar no marketplace..." value={search} onInput={(e: any) => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Featured strip */}
        {!loading && featured.length > 0 && (
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Destaques</h2>
            <div style={S.featuredStrip}>
              {featured.map(item => {
                const meta = parseMeta(item);
                return (
                  <div key={item.id} style={S.featuredCard}
                    onClick={() => { window.location.href = `/${locale}/${item.slug}`; }}
                    onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.borderColor = T.primaryBg; }}
                    onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = T.border; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      {meta.icon_url
                        ? <img src={meta.icon_url} alt={item.title} style={{ width: 40, height: 40, borderRadius: 8 }} loading="lazy" />
                        : <div style={{ width: 40, height: 40, borderRadius: 8, background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text, fontWeight: 700 }}>{(item.title || '?')[0]}</div>}
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{item.title}</div>
                        <div style={{ fontSize: 12, color: T.textMuted }}>{item.category_name}</div>
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.5, margin: 0 }}>{item.excerpt}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Category filter */}
        {categories.length > 0 && (
          <div style={S.catBar}>
            <span style={S.catPill(!activeCat, T.primary)} onClick={() => setActiveCat('')}>
              Todas ({items.length})
            </span>
            {categories.map(cat => (
              <span key={cat.id} style={S.catPill(activeCat === cat.slug, cat.color || T.primary)}
                onClick={() => setActiveCat(activeCat === cat.slug ? '' : cat.slug)}>
                {cat.name} ({cat.entry_count})
              </span>
            ))}
          </div>
        )}

        {/* Grid */}
        <div style={S.grid}>
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            : filtered.map(item => <ItemCard key={item.id} item={item} locale={locale} />)}
        </div>
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.textMuted }}>
            <p style={{ fontSize: 16 }}>Nenhum item encontrado</p>
          </div>
        )}

        {/* Footer */}
        <div style={S.footer}>
          <p>WhiteLabelClaw - Plataforma de automacao e deploy</p>
        </div>
      </div>
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
    </div>
  );
}
